export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3333/api/v1';

export interface ApiIssue {
  path: string;
  message: string;
}

/** Erro de resposta HTTP não-ok (4xx/5xx) — servidor respondeu, não é o caso de retry de rede. */
export class ApiError extends Error {
  readonly status: number;
  readonly issues: ApiIssue[] | undefined;

  constructor(status: number, message: string, issues?: ApiIssue[]) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.issues = issues;
  }

  /** 4xx (exceto 401, tratado à parte pelo refresh) — reenviar não vai resolver. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }
}

/** fetch() nunca chegou a uma resposta (sem rede) — candidato a nova tentativa depois. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Falha de rede — sem conexão com o servidor', { cause });
    this.name = 'NetworkError';
  }
}

export interface HttpRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

interface ErrorBody {
  message?: unknown;
  issues?: unknown;
}

function asErrorBody(data: unknown): ErrorBody {
  return typeof data === 'object' && data !== null ? (data as ErrorBody) : {};
}

function extractIssues(data: unknown): ApiIssue[] | undefined {
  const { issues } = asErrorBody(data);
  return Array.isArray(issues) ? (issues as ApiIssue[]) : undefined;
}

function extractMessage(data: unknown, fallback: string): string {
  const { message } = asErrorBody(data);
  if (typeof message === 'string') return message;
  if (Array.isArray(message) && typeof message[0] === 'string') return message[0];
  return fallback;
}

/** Requisição HTTP crua — sem autenticação. Usada pelo módulo de sessão (login/refresh). */
export async function httpRequest<T>(path: string, options: HttpRequestOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers: { 'content-type': 'application/json', ...options.headers },
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
  };

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, init);
  } catch (cause) {
    throw new NetworkError(cause);
  }

  const text = await response.text();
  const data: unknown = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      extractMessage(data, response.statusText),
      extractIssues(data),
    );
  }
  return data as T;
}
